import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { isWebsummitContatoUrl } from '../constants/websummit-contato.constants';

import { UtmStorageService } from './utm-storage.service';

@Injectable({
  providedIn: 'root',
})
export class UtmPreservationService {
  private destroy$ = new Subject<void>();
  public preservedUtmParams: Record<string, string> = {};
  private isInitialized = false;
  private hadUtmSourceInInitialUrl = false;
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private utmStorageService = inject(UtmStorageService);


  initialize(): void {
    if (!isPlatformBrowser(this.platformId) || this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    // IMPORTANT: We only preserve/apply UTM if it is present in the URL.
    // If the user enters the app WITHOUT utm_* in the URL, we must NOT resurrect old UTM from cookies/session.
    const initialUrlParams = new URLSearchParams(window.location.search);
    const initialHasAnyUtm = Array.from(initialUrlParams.keys()).some(k => k.toLowerCase().startsWith('utm_'));

    this.hadUtmSourceInInitialUrl = initialHasAnyUtm;

    if (!initialHasAnyUtm) {
      // Hard reset: user started a "no UTM" session.
      this.preservedUtmParams = {};
      this.utmStorageService.clearTrackingData();
    } else {
      this.preservedUtmParams = {};
      this.captureUtmFromCurrentUrl();
    }

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.preserveUtmInNavigation(event.url);
      });
  }

  /**
   * Capture UTM params from the current URL and persist them.
   * If UTM is found, this enables preservation for the current SPA session.
   */
  private captureUtmFromCurrentUrl(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const utmParams: Record<string, string> = {};

    urlParams.forEach((value, key) => {
      if (key.toLowerCase().startsWith('utm_')) {
        utmParams[key.toLowerCase()] = value;
      }
    });

    if (Object.keys(utmParams).length > 0) {
      this.hadUtmSourceInInitialUrl = true;
      this.preservedUtmParams = { ...this.preservedUtmParams, ...utmParams };
      
      const trackingData = this.utmStorageService.getTrackingData();
      this.utmStorageService.persistTrackingData(
        trackingData.channel_id,
        this.preservedUtmParams
      );
    }
  }


  private preserveUtmInNavigation(url: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (isWebsummitContatoUrl(url)) {
      return;
    }

    // If the navigation includes UTM in URL, capture it (and enable preservation).
    this.captureUtmFromCurrentUrl();

    if (!this.hadUtmSourceInInitialUrl || Object.keys(this.preservedUtmParams).length === 0) {
      return;
    }

    setTimeout(() => {
      try {
        const currentUrl = new URL(window.location.href);
        const currentParams = new URLSearchParams(currentUrl.search);
        
        const hasUtmParams = Array.from(currentParams.keys()).some(key => 
          key.toLowerCase().startsWith('utm_')
        );

        const eventUrlTree = this.router.parseUrl(url);
        const hasUtmInEventUrl = Object.keys(eventUrlTree.queryParams || {}).some(key =>
          key.toLowerCase().startsWith('utm_')
        );

        if (hasUtmParams || hasUtmInEventUrl) {
          return;
        }

        const tree = this.router.parseUrl(url);
        const nextQueryParams: Record<string, any> = { ...(tree.queryParams || {}) };

        // Preserve any non-UTM params from the *current* URL (more reliable than event.url)
        currentParams.forEach((value, key) => {
          if (!key.toLowerCase().startsWith('utm_')) {
            nextQueryParams[key] = value;
          }
        });

        const normalizedExistingKeys = new Set(Object.keys(nextQueryParams).map(k => k.toLowerCase()));
        Object.keys(this.preservedUtmParams).forEach(key => {
          if (!normalizedExistingKeys.has(key.toLowerCase())) {
            nextQueryParams[key] = this.preservedUtmParams[key];
          }
        });

        tree.queryParams = nextQueryParams;

        this.router.navigateByUrl(tree, { replaceUrl: true });
      } catch (error) {
        console.error('Erro ao preservar parâmetros UTM:', error);
      }
    }, 50);
  }

  addPreservedUtmToUrl(url: string): string {
    if (!this.hadUtmSourceInInitialUrl || Object.keys(this.preservedUtmParams).length === 0) {
      return url;
    }

    try {
      const urlObj = new URL(url, window.location.origin);
      
      const hasUtmInUrl = Array.from(urlObj.searchParams.keys()).some(key => 
        key.toLowerCase().startsWith('utm_')
      );
      
      if (hasUtmInUrl) {
        return url;
      }
      
      Object.keys(this.preservedUtmParams).forEach(key => {
        if (!urlObj.searchParams.has(key)) {
          urlObj.searchParams.set(key, this.preservedUtmParams[key]);
        }
      });

      return urlObj.toString();
    } catch {
      return url;
    }
  }

  addPreservedUtmToQueryParams(queryParams: Record<string, any>): Record<string, any> {
    if (!this.hadUtmSourceInInitialUrl || Object.keys(this.preservedUtmParams).length === 0) {
      return { ...queryParams };
    }
    
    const hasUtmInQueryParams = Object.keys(queryParams).some(key => 
      key.toLowerCase().startsWith('utm_')
    );
    
    if (hasUtmInQueryParams) {
      return { ...queryParams };
    }
    
    const result = { ...queryParams };
    
    const normalizedExistingKeys = new Map<string, string>();
    Object.keys(queryParams).forEach(key => {
      normalizedExistingKeys.set(key.toLowerCase(), key);
    });
    
    Object.keys(this.preservedUtmParams).forEach(key => {
      const normalizedKey = key.toLowerCase();
      if (!normalizedExistingKeys.has(normalizedKey)) {
        result[key] = this.preservedUtmParams[key];
      }
    });

    return result;
  }

  isUtmEnabled(): boolean {
    return this.hadUtmSourceInInitialUrl;
  }

  clearPreservedUtm(): void {
    this.preservedUtmParams = {};
    this.hadUtmSourceInInitialUrl = false;
    this.utmStorageService.clearTrackingData();
  }

  /**
   * Disables UTM mode and removes utm_* params from the current URL.
   * Use when utm_source is considered invalid (e.g., channel INACTIVE) so the app behaves like "no UTM".
   */
  disableUtmAndCleanUrl(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const currentUrl = new URL(window.location.href);
      const params = new URLSearchParams(currentUrl.search);

      // Remove all utm_* params
      Array.from(params.keys()).forEach((key) => {
        if (key.toLowerCase().startsWith('utm_')) {
          params.delete(key);
        }
      });

      // Disable UTM preservation/tracking
      this.clearPreservedUtm();

      const pathname = currentUrl.pathname;
      const remaining = params.toString();
      const newUrl = remaining ? `${pathname}?${remaining}` : pathname;

      this.router.navigateByUrl(newUrl, { replaceUrl: true }).catch(() => {
        // Navigation replacement is best-effort after UTM cleanup.
      });
    } catch (error) {
      console.error('Erro ao desativar UTM e limpar URL:', error);
      this.clearPreservedUtm();
    }
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.isInitialized = false;
  }
}

